import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ClipboardCheck, PlayCircle, CheckCircle2, XCircle, AlertCircle, TrendingUp, CheckSquare, Square, ShieldAlert, Siren, Upload, Search, Pill } from "lucide-react";
import { getStatusLabel } from "@/lib/statusLabels";

const COMPARISONS = [
  { v: "abaixo_ideal", l: "Abaixo do ideal" },
  { v: "acima_ideal", l: "Acima do ideal" },
  { v: "fora_ideal", l: "Fora do ideal (qualquer lado)" },
  { v: "fora_referencia", l: "Fora da referência tradicional" },
  { v: "entre", l: "Entre (min e max)" },
  { v: "alterado", l: "Alterado (fora dos limites)" },
  { v: "<", l: "< (menor que)" },
  { v: "<=", l: "≤ (menor ou igual)" },
  { v: ">", l: "> (maior que)" },
  { v: ">=", l: "≥ (maior ou igual)" },
  { v: "=", l: "= (igual a)" },
];

const OPS_WITH_VALUE = new Set(["<", "<=", ">", ">=", "="]);

const STATUS_OPTIONS = [
  { v: "ativo", l: "Ativo", cls: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  { v: "rascunho", l: "Rascunho", cls: "bg-amber-500/15 text-amber-700 border-amber-500/30" },
  { v: "arquivado", l: "Arquivado", cls: "bg-muted text-muted-foreground border-border" },
];

const CONFIDENCE_OPTIONS = [
  { v: "alta", l: "Alta" },
  { v: "media", l: "Moderada" },
  { v: "baixa", l: "Sugestiva" },
];

const GRAVITY_OPTIONS = [
  { v: "eletivo", l: "Eletivo", cls: "border-border bg-muted/40 text-muted-foreground" },
  { v: "urgencia", l: "Urgência", cls: "border-amber-500/40 bg-amber-500/10 text-amber-700" },
  { v: "emergencia", l: "Emergência", cls: "border-rose-500/40 bg-rose-500/10 text-rose-700" },
];

const gravityMeta = (v) => GRAVITY_OPTIONS.find((g) => g.v === (v || "eletivo")) || GRAVITY_OPTIONS[0];

const compLabel = (v) => COMPARISONS.find((c) => c.v === v)?.l || v;
const statusMeta = (v) => STATUS_OPTIONS.find((s) => s.v === (v || "ativo")) || STATUS_OPTIONS[0];

const emptyPattern = () => ({
  name: "", category: "", subcategoria: "", codigo_interno: "",
  status: "rascunho", nivel_confianca: "media", fonte: "",
  gravidade: "eletivo", is_alerta_medico: false,
  hipoteses: [""], mecanismo: "",
  exames_complementares: [""], tags: [""],
  nutritional_hypotheses: "",
  suggested_tests: "", explanation: "",
  conditions: [{ marker_name: "", comparison: "abaixo_ideal", value: "", value_min: "", value_max: "" }],
});

function TestPatternModal({ conditions, markers, drugs = [], onClose }) {
  // Marker conditions → numeric value; drug conditions → yes/no presence.
  const markerConds = conditions.filter((c) => (c.type || "marker") === "marker" && c.marker_name);
  const drugConds = conditions.filter((c) => (c.type || "marker") === "uso_de_farmaco" && c.farmaco_codigo);
  const [values, setValues] = useState(() =>
    markerConds.map((c) => ({ marker_name: c.marker_name, value: "" })),
  );
  const [drugPresence, setDrugPresence] = useState(() => {
    const p = {};
    for (const c of drugConds) p[c.farmaco_codigo] = true; // default: in use
    return p;
  });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const drugNameByCode = (drugs || []).reduce((acc, d) => { acc[d.codigo] = d.nome; return acc; }, {});

  const updateValue = (i, v) => {
    const next = [...values];
    next[i] = { ...next[i], value: v };
    setValues(next);
  };

  const run = async () => {
    if (values.some((v) => v.value === "" || Number.isNaN(Number(v.value)))) {
      toast.error("Informe valores numéricos para todos os marcadores.");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const payload = {
        conditions: conditions.map((c) => {
          const t = c.type || "marker";
          if (t === "uso_de_farmaco") {
            return { type: "uso_de_farmaco", farmaco_codigo: c.farmaco_codigo };
          }
          return {
            type: "marker",
            marker_name: c.marker_name,
            comparison: c.comparison,
            value: c.value === "" || c.value === undefined || c.value === null ? null : Number(c.value),
            value_min: c.value_min === "" || c.value_min === undefined ? null : Number(c.value_min),
            value_max: c.value_max === "" || c.value_max === undefined ? null : Number(c.value_max),
          };
        }),
        values: values.map((v) => ({ marker_name: v.marker_name, value: Number(v.value) })),
        drug_presence: drugPresence,
      };
      const { data } = await api.post("/admin/clinical-patterns/test", payload);
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao testar padrão.");
    }
    setRunning(false);
  };

  const totalInputs = values.length + drugConds.length;

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] grid place-items-center p-4" onClick={onClose} data-testid="test-pattern-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1 inline-flex items-center gap-1"><PlayCircle className="w-3.5 h-3.5" /> Simulação</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Testar padrão</h3>
            <p className="text-xs text-muted-foreground mt-1">Informe valores fictícios para simular o disparo deste padrão.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="test-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          {totalInputs === 0 ? (
            <div className="text-sm text-muted-foreground italic">Adicione condições com marcador ou fármaco antes de testar.</div>
          ) : null}
          {values.map((v, i) => (
            <div key={`m-${i}`} className="flex items-center gap-2" data-testid={`test-value-${i}`}>
              <div className="flex-1 text-sm font-medium">{v.marker_name}</div>
              <input
                type="number" step="0.01" value={v.value}
                onChange={(e) => updateValue(i, e.target.value)}
                className="w-32 px-3 py-1.5 rounded-sm border border-input bg-card text-sm"
                placeholder="Valor"
                data-testid={`test-value-input-${i}`}
              />
            </div>
          ))}
          {drugConds.map((c, i) => {
            const code = c.farmaco_codigo;
            const isYes = !!drugPresence[code];
            return (
              <div key={`d-${i}`} className="flex items-center gap-2" data-testid={`test-drug-${i}`}>
                <div className="flex-1 text-sm font-medium inline-flex items-center gap-2">
                  <Pill className="w-3.5 h-3.5 text-gold" />
                  {drugNameByCode[code] || code}
                  <span className="text-[10px] text-muted-foreground">{code}</span>
                </div>
                <div className="inline-flex rounded-sm border border-input overflow-hidden text-xs">
                  <button type="button"
                    onClick={() => setDrugPresence({ ...drugPresence, [code]: true })}
                    className={`px-3 py-1.5 font-semibold ${isYes ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                    data-testid={`test-drug-yes-${i}`}
                  >Sim</button>
                  <button type="button"
                    onClick={() => setDrugPresence({ ...drugPresence, [code]: false })}
                    className={`px-3 py-1.5 font-semibold border-l border-input ${!isYes ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                    data-testid={`test-drug-no-${i}`}
                  >Não</button>
                </div>
              </div>
            );
          })}
          {totalInputs > 0 && (
            <button onClick={run} disabled={running}
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
              data-testid="run-test-btn">
              <PlayCircle className="w-4 h-4" /> {running ? "Testando…" : "Rodar teste"}
            </button>
          )}
          {result && (
            <div className={`rounded-sm border p-3 text-sm space-y-2 ${result.triggered ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="test-result">
              <div className="flex items-center gap-2 font-semibold">
                {result.triggered ? (
                  <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Padrão dispararia com estes valores.</>
                ) : (
                  <><XCircle className="w-4 h-4 text-amber-600" /> Padrão não dispararia com estes valores.</>
                )}
              </div>
              <ul className="space-y-1 text-xs">
                {(result.conditions || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    {c.matched ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5" /> : <XCircle className="w-3.5 h-3.5 text-amber-600 mt-0.5" />}
                    <span>
                      {c.type === "uso_de_farmaco" ? (
                        <>
                          <span className="font-medium">Fármaco em uso:</span>{" "}
                          <span className="text-muted-foreground">{drugNameByCode[c.farmaco_codigo] || c.farmaco_codigo}</span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium">{c.marker_name}</span>{" "}
                          <span className="text-muted-foreground">{compLabel(c.comparison)}</span>
                          {c.value !== undefined && <span className="text-muted-foreground"> · valor {c.value}</span>}
                        </>
                      )}
                      {c.reason && <span className="text-muted-foreground italic"> — {c.reason}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const NEW_MARKER_COND = () => ({ type: "marker", marker_name: "", comparison: "abaixo_ideal", value: "", value_min: "", value_max: "", farmaco_codigo: "" });
const NEW_DRUG_COND = () => ({ type: "uso_de_farmaco", marker_name: "", comparison: "abaixo_ideal", value: "", value_min: "", value_max: "", farmaco_codigo: "" });

function PatternModal({ initial, markers, drugs = [], onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!initial) return emptyPattern();
    return {
      ...emptyPattern(),
      ...initial,
      hipoteses: (initial.hipoteses && initial.hipoteses.length ? initial.hipoteses : (initial.nutritional_hypotheses ? [initial.nutritional_hypotheses] : [""])),
      exames_complementares: (initial.exames_complementares && initial.exames_complementares.length
        ? initial.exames_complementares
        : (initial.suggested_tests ? [initial.suggested_tests] : [""])),
      tags: (initial.tags && initial.tags.length ? initial.tags : [""]),
      conditions: (initial.conditions && initial.conditions.length
        ? initial.conditions.map((c) => ({ ...NEW_MARKER_COND(), ...c, type: c.type || "marker" }))
        : [NEW_MARKER_COND()]),
    };
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const updateCond = (i, patch) => {
    const next = [...form.conditions];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, conditions: next });
  };
  const addCond = () => setForm({ ...form, conditions: [...form.conditions, NEW_MARKER_COND()] });
  const addDrugCond = () => setForm({ ...form, conditions: [...form.conditions, NEW_DRUG_COND()] });
  const removeCond = (i) => setForm({ ...form, conditions: form.conditions.filter((_, ii) => ii !== i) });

  const updateHyp = (i, v) => {
    const next = [...form.hipoteses];
    next[i] = v;
    setForm({ ...form, hipoteses: next });
  };
  const addHyp = () => setForm({ ...form, hipoteses: [...form.hipoteses, ""] });
  const removeHyp = (i) => setForm({ ...form, hipoteses: form.hipoteses.filter((_, ii) => ii !== i) });

  const updateArr = (key, i, v) => {
    const next = [...(form[key] || [])];
    next[i] = v;
    setForm({ ...form, [key]: next });
  };
  const addArr = (key) => setForm({ ...form, [key]: [...(form[key] || []), ""] });
  const removeArr = (key, i) => setForm({ ...form, [key]: (form[key] || []).filter((_, ii) => ii !== i) });

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome do padrão."); return; }
    if (form.conditions.length === 0) {
      toast.error("Adicione ao menos uma condição.");
      return;
    }
    for (const c of form.conditions) {
      if ((c.type || "marker") === "uso_de_farmaco") {
        if (!c.farmaco_codigo) {
          toast.error("Selecione um fármaco em todas as condições de 'Fármaco em uso'.");
          return;
        }
        continue;
      }
      if (!c.marker_name) {
        toast.error("Selecione um marcador em todas as condições.");
        return;
      }
      if (c.comparison === "entre" && (c.value_min === "" || c.value_max === "" || c.value_min == null || c.value_max == null)) {
        toast.error("Operador 'entre' exige valor mínimo e máximo.");
        return;
      }
      if (OPS_WITH_VALUE.has(c.comparison) && (c.value === "" || c.value == null)) {
        toast.error(`Operador '${c.comparison}' exige um valor.`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category || "",
        subcategoria: form.subcategoria || "",
        codigo_interno: form.codigo_interno || "",
        status: form.status || "rascunho",
        nivel_confianca: form.nivel_confianca || "media",
        fonte: form.fonte || "",
        gravidade: form.gravidade || "eletivo",
        is_alerta_medico: !!form.is_alerta_medico,
        mecanismo: form.mecanismo || "",
        hipoteses: (form.hipoteses || []).map((h) => (h || "").trim()).filter(Boolean),
        exames_complementares: (form.exames_complementares || []).map((e) => (e || "").trim()).filter(Boolean),
        tags: (form.tags || []).map((t) => (t || "").trim()).filter(Boolean),
        nutritional_hypotheses: form.nutritional_hypotheses || "",
        suggested_tests: form.suggested_tests || "",
        explanation: form.explanation || "",
        conditions: form.conditions.map((c) => {
          if ((c.type || "marker") === "uso_de_farmaco") {
            return { type: "uso_de_farmaco", farmaco_codigo: c.farmaco_codigo };
          }
          return {
            type: "marker",
            marker_name: c.marker_name,
            comparison: c.comparison,
            value: OPS_WITH_VALUE.has(c.comparison) && c.value !== "" ? Number(c.value) : null,
            value_min: c.comparison === "entre" && c.value_min !== "" ? Number(c.value_min) : null,
            value_max: c.comparison === "entre" && c.value_max !== "" ? Number(c.value_max) : null,
          };
        }),
      };
      if (form.id) await api.patch(`/admin/clinical-patterns/${form.id}`, payload);
      else await api.post("/admin/clinical-patterns", payload);
      toast.success(form.id ? "Padrão atualizado." : "Padrão criado.");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar."); }
    setSaving(false);
  };

  const canTest = form.conditions.length > 0 && form.conditions.every((c) =>
    (c.type || "marker") === "uso_de_farmaco" ? !!c.farmaco_codigo : !!c.marker_name,
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="pattern-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1">{form.id ? "Editar padrão" : "Novo padrão clínico"}</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">{form.id ? form.name : "Cadastrar padrão"}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm" data-testid="pattern-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nome do padrão</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="pattern-name-input" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Categoria</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: Eritrograma" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Subcategoria</label>
              <input value={form.subcategoria} onChange={(e) => setForm({ ...form, subcategoria: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: Anemias" data-testid="pattern-subcategory-input" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Fonte</label>
              <input value={form.fonte} onChange={(e) => setForm({ ...form, fonte: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: Guyton 14ª ed.; Kasper (Harrison)" data-testid="pattern-source-input" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="pattern-status-select">
                {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Nível de confiança</label>
              <select value={form.nivel_confianca} onChange={(e) => setForm({ ...form, nivel_confianca: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="pattern-confidence-select">
                {CONFIDENCE_OPTIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Código interno</label>
              <input value={form.codigo_interno} onChange={(e) => setForm({ ...form, codigo_interno: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                placeholder="Ex: ERI-001" data-testid="pattern-codigo-input" />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-1 block">Gravidade</label>
              <select value={form.gravidade} onChange={(e) => setForm({ ...form, gravidade: e.target.value })}
                className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
                data-testid="pattern-gravity-select">
                {GRAVITY_OPTIONS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </div>
          </div>

          <div className={`rounded-sm border p-3 ${form.is_alerta_medico ? "border-rose-500/40 bg-rose-500/5" : "border-border bg-muted/30"}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={!!form.is_alerta_medico} onChange={(e) => setForm({ ...form, is_alerta_medico: e.target.checked })}
                className="mt-1 accent-rose-600" data-testid="pattern-alerta-medico-toggle" />
              <div className="flex-1">
                <div className="text-sm font-semibold inline-flex items-center gap-2">
                  <ShieldAlert className={`w-4 h-4 ${form.is_alerta_medico ? "text-rose-600" : "text-muted-foreground"}`} />
                  Padrão de alerta médico
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Quando ativado, sinaliza encaminhamento médico prioritário e as hipóteses nutricionais são ocultadas na análise.
                </div>
              </div>
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <label className="overline text-muted-foreground">Condições (avaliadas com &quot;E&quot;)</label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={addCond} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="pattern-add-condition">
                  <Plus className="w-3 h-3" /> Adicionar marcador
                </button>
                <button type="button" onClick={addDrugCond} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="pattern-add-drug-condition">
                  <Plus className="w-3 h-3" /> Adicionar fármaco em uso
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {form.conditions.map((c, i) => {
                const ctype = c.type || "marker";
                return (
                  <div key={i} className="space-y-1" data-testid={`pattern-cond-${i}`}>
                    <div className="flex gap-2 items-center">
                      <span
                        className={`px-2 py-1 rounded-sm border text-[10px] uppercase tracking-wider font-semibold ${
                          ctype === "uso_de_farmaco"
                            ? "border-gold/40 bg-gold/10 text-gold"
                            : "border-border bg-muted/40 text-muted-foreground"
                        }`}
                        data-testid={`pattern-cond-type-${i}`}
                      >
                        {ctype === "uso_de_farmaco" ? "Fármaco" : "Marcador"}
                      </span>
                      {ctype === "uso_de_farmaco" ? (
                        <select
                          value={c.farmaco_codigo || ""}
                          onChange={(e) => updateCond(i, { farmaco_codigo: e.target.value })}
                          className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm"
                          data-testid={`pattern-cond-drug-${i}`}
                        >
                          <option value="">Selecione o fármaco…</option>
                          {drugs.map((d) => (
                            <option key={d.codigo} value={d.codigo}>
                              {d.codigo} — {d.nome}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <select value={c.marker_name} onChange={(e) => updateCond(i, { marker_name: e.target.value })}
                            className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm">
                            <option value="">Selecione o marcador…</option>
                            {markers.map((m) => <option key={m.id} value={m.name}>{m.name} ({m.category})</option>)}
                          </select>
                          <select value={c.comparison} onChange={(e) => updateCond(i, { comparison: e.target.value })}
                            className="px-3 py-2 rounded-sm border border-input bg-card text-sm"
                            data-testid={`pattern-cond-op-${i}`}>
                            {COMPARISONS.map((cp) => <option key={cp.v} value={cp.v}>{cp.l}</option>)}
                          </select>
                        </>
                      )}
                      <button type="button" onClick={() => removeCond(i)} className="p-2 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5" aria-label="Remover">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {ctype === "marker" && c.comparison === "entre" && (
                      <div className="flex gap-2 items-center pl-1">
                        <input type="number" step="0.01" value={c.value_min ?? ""} onChange={(e) => updateCond(i, { value_min: e.target.value })}
                          placeholder="Min" className="w-32 px-3 py-1.5 rounded-sm border border-input bg-card text-xs"
                          data-testid={`pattern-cond-min-${i}`} />
                        <span className="text-xs text-muted-foreground">e</span>
                        <input type="number" step="0.01" value={c.value_max ?? ""} onChange={(e) => updateCond(i, { value_max: e.target.value })}
                          placeholder="Max" className="w-32 px-3 py-1.5 rounded-sm border border-input bg-card text-xs"
                          data-testid={`pattern-cond-max-${i}`} />
                      </div>
                    )}
                    {ctype === "marker" && OPS_WITH_VALUE.has(c.comparison) && (
                      <div className="flex gap-2 items-center pl-1">
                        <input type="number" step="0.01" value={c.value ?? ""} onChange={(e) => updateCond(i, { value: e.target.value })}
                          placeholder="Valor" className="w-32 px-3 py-1.5 rounded-sm border border-input bg-card text-xs"
                          data-testid={`pattern-cond-value-${i}`} />
                        <span className="text-[11px] text-muted-foreground italic">unidade do marcador da Biblioteca</span>
                      </div>
                    )}
                    {ctype === "marker" && c.comparison === "alterado" && (
                      <div className="pl-1 text-[11px] text-muted-foreground italic inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Dispara quando o valor sai dos limites (ideal ou referência) da Biblioteca.
                      </div>
                    )}
                    {ctype === "uso_de_farmaco" && (
                      <div className="pl-1 text-[11px] text-muted-foreground italic">
                        Dispara quando o paciente tem esse fármaco vinculado com status &quot;Ativo&quot;.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!form.is_alerta_medico && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline text-muted-foreground">Hipóteses nutricionais (uma por campo)</label>
              <button type="button" onClick={addHyp} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="pattern-add-hypothesis">
                <Plus className="w-3 h-3" /> Adicionar hipótese
              </button>
            </div>
            <div className="space-y-2">
              {form.hipoteses.map((h, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`pattern-hyp-${i}`}>
                  <input value={h} onChange={(e) => updateHyp(i, e.target.value)}
                    className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    placeholder="Ex: Deficiência de ferro" />
                  {form.hipoteses.length > 1 && (
                    <button type="button" onClick={() => removeHyp(i)} className="p-2 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5" aria-label="Remover">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          )}
          {form.is_alerta_medico && (
            <div className="rounded-sm border border-rose-500/40 bg-rose-500/5 p-3 text-xs text-rose-700 inline-flex items-center gap-2" data-testid="alerta-medico-notice">
              <Siren className="w-4 h-4" /> Padrão de alerta médico — a análise não exibirá hipóteses nutricionais, apenas a recomendação de encaminhamento médico.
            </div>
          )}

          <div>
            <label className="overline text-muted-foreground mb-1 block">Mecanismo (explicação fisiopatológica)</label>
            <textarea rows={3} value={form.mecanismo} onChange={(e) => setForm({ ...form, mecanismo: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Explique brevemente o mecanismo fisiopatológico deste padrão." data-testid="pattern-mecanismo-input" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline text-muted-foreground">Exames complementares</label>
              <button type="button" onClick={() => addArr("exames_complementares")} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <Plus className="w-3 h-3" /> Adicionar exame
              </button>
            </div>
            <div className="space-y-2">
              {(form.exames_complementares || [""]).map((e, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`pattern-exame-${i}`}>
                  <input value={e} onChange={(ev) => updateArr("exames_complementares", i, ev.target.value)}
                    className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    placeholder="Ex: Ferritina" />
                  {form.exames_complementares.length > 1 && (
                    <button type="button" onClick={() => removeArr("exames_complementares", i)} className="p-2 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5" aria-label="Remover">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="overline text-muted-foreground">Tags (livres)</label>
              <button type="button" onClick={() => addArr("tags")} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <Plus className="w-3 h-3" /> Adicionar tag
              </button>
            </div>
            <div className="space-y-2">
              {(form.tags || [""]).map((t, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`pattern-tag-${i}`}>
                  <input value={t} onChange={(ev) => updateArr("tags", i, ev.target.value)}
                    className="flex-1 px-3 py-2 rounded-sm border border-input bg-card text-sm"
                    placeholder="Ex: hemograma, anemia" />
                  {form.tags.length > 1 && (
                    <button type="button" onClick={() => removeArr("tags", i)} className="p-2 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5" aria-label="Remover">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="overline text-muted-foreground mb-1 block">Exames sugeridos (legado — texto livre)</label>
            <input value={form.suggested_tests} onChange={(e) => setForm({ ...form, suggested_tests: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Opcional — prefira 'Exames complementares' acima" />
          </div>
          <div>
            <label className="overline text-muted-foreground mb-1 block">Explicação (racional clínico)</label>
            <textarea rows={4} value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-sm"
              placeholder="Explicação livre — preencha conforme material próprio, livros ou consenso." />
          </div>

          <div className="flex gap-2 justify-between pt-2 flex-wrap">
            <button type="button" onClick={() => setTesting(true)} disabled={!canTest}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-primary text-primary text-sm font-semibold hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pattern-test-btn">
              <PlayCircle className="w-4 h-4" /> Testar padrão
            </button>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted">Cancelar</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60" data-testid="pattern-save-btn">
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {testing && (
        <TestPatternModal
          conditions={form.conditions}
          markers={markers}
          drugs={drugs}
          onClose={() => setTesting(false)}
        />
      )}
    </div>
  );
}

function BulkImportModal({ onClose, onImported }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [parseError, setParseError] = useState("");

  const parse = () => {
    try {
      const trimmed = text.trim();
      if (!trimmed) throw new Error("Cole ao menos um padrão em JSON.");
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      setParseError("");
      return arr;
    } catch (e) {
      setParseError(e.message || "JSON inválido.");
      return null;
    }
  };

  const validate = async () => {
    const arr = parse();
    if (!arr) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/clinical-patterns/import", { patterns: arr, dry_run: true });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao validar.");
    }
    setRunning(false);
  };

  const commit = async () => {
    if (!result || result.error_count > 0) {
      toast.error("Corrija os erros antes de semear.");
      return;
    }
    const arr = parse();
    if (!arr) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/clinical-patterns/import", { patterns: arr, dry_run: false });
      toast.success(`${data.inserted_ids.length} padrão(ões) semeado(s) com sucesso.`);
      setText("");
      setResult(null);
      onImported();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao semear.");
    }
    setCommitting(false);
  };

  const sample = JSON.stringify([{
    name: "Anemia ferropriva",
    category: "Eritrograma",
    subcategoria: "Anemias",
    codigo_interno: "ERI-001",
    nivel_confianca: "alta",
    gravidade: "eletivo",
    fonte: "Guyton 14ª ed.",
    hipoteses: ["Deficiência de ferro", "Perda sanguínea crônica"],
    mecanismo: "Redução da eritropoiese por baixa disponibilidade de ferro.",
    exames_complementares: ["Ferritina", "Saturação de transferrina"],
    tags: ["hemograma", "anemia"],
    conditions: [
      { marker_name: "Hemoglobina", comparison: "abaixo_ideal" },
      { marker_name: "VCM", comparison: "<", value: 80 },
    ],
  }], null, 2);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={onClose} data-testid="bulk-import-modal">
      <div className="bg-card border border-border rounded-sm p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline text-gold mb-1 inline-flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Seed</div>
            <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Importar padrões em massa</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Cole um array JSON com os padrões. Cada condição precisa referenciar um marcador que já existe na Biblioteca (usar o nome exato).
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="overline text-muted-foreground">JSON</label>
              <button type="button" onClick={() => setText(sample)}
                className="text-xs text-primary hover:underline" data-testid="load-sample-btn">
                Carregar exemplo
              </button>
            </div>
            <textarea
              value={text} onChange={(e) => setText(e.target.value)}
              rows={12}
              className="w-full px-3 py-2 rounded-sm border border-input bg-card text-xs font-mono"
              placeholder='[ { "name": "...", "conditions": [ { "marker_name": "...", "comparison": "abaixo_ideal" } ] } ]'
              data-testid="import-textarea"
            />
            {parseError && (
              <div className="text-xs text-rose-700 mt-1 inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {parseError}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={validate} disabled={running}
              className="inline-flex items-center gap-2 rounded-sm border border-primary text-primary px-4 py-2 text-sm font-semibold hover:bg-primary/5 disabled:opacity-60"
              data-testid="import-validate-btn">
              <PlayCircle className="w-4 h-4" /> {running ? "Validando…" : "Validar (dry-run)"}
            </button>
            <button onClick={commit} disabled={!result || result.error_count > 0 || committing}
              className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="import-commit-btn">
              <CheckCircle2 className="w-4 h-4" /> {committing ? "Semeando…" : "Semear no banco"}
            </button>
          </div>

          {result && (
            <div className={`rounded-sm border p-3 space-y-2 text-sm ${result.error_count === 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`} data-testid="import-result">
              <div className="font-semibold">
                {result.ok} de {result.total} padrão(ões) válido(s){result.error_count > 0 ? ` · ${result.error_count} com erro` : ""}.
              </div>
              <ul className="space-y-1 text-xs max-h-64 overflow-y-auto">
                {result.results.map((r) => (
                  <li key={r.index} className="flex items-start gap-2" data-testid={`import-item-${r.index}`}>
                    {r.status === "ok"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-none" />
                      : <XCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 flex-none" />}
                    <span>
                      <span className="font-medium">#{r.index + 1} {r.name || "(sem nome)"}</span>
                      {r.status === "error" && <span className="text-rose-700"> — {r.reason}</span>}
                      {r.status === "ok" && r.preview && (
                        <span className="text-muted-foreground">
                          {" "}— {r.preview.condition_count} condição(ões) · {getStatusLabel("pattern", r.preview.status).label} · {r.preview.gravidade}
                        </span>
                      )}
                      {r.status === "ok" && r.preview?.synonyms_resolved?.length > 0 && (
                        <div className="text-[11px] text-amber-700 mt-0.5 pl-1" data-testid={`synonym-note-${r.index}`}>
                          Sinônimos resolvidos: {r.preview.synonyms_resolved.join(" · ")}
                        </div>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {result.error_count === 0 && !result.dry_run && (
                <div className="text-emerald-700 font-semibold">Semeadura concluída.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



export default function AdminClinicalPatternsPage() {
  const [patterns, setPatterns] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("todos");
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("todas");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = () => setSelectedIds(new Set(filtered.map((p) => p.id)));

  const bulkUpdate = async (status) => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await api.post("/admin/clinical-patterns/bulk-status", {
        ids: Array.from(selectedIds),
        status,
      });
      toast.success(`${data.modified} padrão(ões) marcados como ${statusMeta(status).l}.`);
      clearSelection();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro na atualização em massa."); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [p, m, d] = await Promise.all([
        api.get("/admin/clinical-patterns"),
        api.get("/admin/markers"),
        api.get("/admin/drugs"),
      ]);
      setPatterns(p.data || []);
      setMarkers(m.data || []);
      setDrugs(d.data || []);
    } catch (_) {/* noop */}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return patterns.filter((p) => {
      if (statusFilter !== "todos" && (p.status || "ativo") !== statusFilter) return false;
      if (categoryFilter !== "todas" && (p.category || "Sem categoria") !== categoryFilter) return false;
      if (!q) return true;
      const name = (p.name || "").toLowerCase();
      const tags = (p.tags || []).join(" ").toLowerCase();
      return name.includes(q) || tags.includes(q);
    });
  }, [patterns, statusFilter, categoryFilter, searchText]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    for (const p of patterns) set.add(p.category || "Sem categoria");
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [patterns]);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of filtered) (g[p.category || "Sem categoria"] = g[p.category || "Sem categoria"] || []).push(p);
    return g;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { todos: patterns.length, ativo: 0, rascunho: 0, arquivado: 0 };
    for (const p of patterns) c[p.status || "ativo"] = (c[p.status || "ativo"] || 0) + 1;
    return c;
  }, [patterns]);

  const remove = async (p) => {
    if (!window.confirm(`Excluir o padrão "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/clinical-patterns/${p.id}`);
      toast.success("Padrão excluído.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir."); }
  };

  const cycleStatus = async (p) => {
    const order = ["ativo", "rascunho", "arquivado"];
    const idx = order.indexOf(p.status || "ativo");
    const next = order[(idx + 1) % order.length];
    try {
      await api.patch(`/admin/clinical-patterns/${p.id}`, { status: next });
      toast.success(`Status: ${statusMeta(next).l}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao alterar status."); }
  };

  return (
    <div className="space-y-6" data-testid="admin-patterns-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-gold mb-3 inline-flex items-center gap-2"><ClipboardCheck className="w-3.5 h-3.5" /> Biblioteca</div>
          <h1 className="brand-wordmark text-3xl md:text-4xl font-bold tracking-tight uppercase text-primary">Padrões clínicos</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Regras determinísticas aplicadas na análise automática de exames. Cada padrão dispara quando TODAS suas condições batem.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)}
            className="inline-flex items-center gap-2 rounded-sm border border-primary text-primary px-3 py-2.5 text-sm font-semibold hover:bg-primary/5 transition-colors"
            data-testid="bulk-import-btn">
            <Upload className="w-4 h-4" /> Importar em massa
          </button>
          <button onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 rounded-sm bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors"
            data-testid="new-pattern-btn">
            <Plus className="w-4 h-4" /> Novo padrão
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="filters-bar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Buscar por nome ou tag…"
            className="w-full pl-10 pr-3 py-2 rounded-sm border border-input bg-card text-sm"
            data-testid="patterns-search-input"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 rounded-sm border border-input bg-card text-sm min-w-[200px]"
          data-testid="patterns-category-select"
        >
          <option value="todas">Todas as categorias</option>
          {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex flex-wrap gap-2" data-testid="status-filter-bar">
          {[
            { v: "todos", l: "Todos" },
            ...STATUS_OPTIONS,
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setStatusFilter(f.v)}
              className={`px-3 py-1.5 rounded-sm border text-xs font-semibold transition-colors ${
                statusFilter === f.v ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
              }`}
              data-testid={`filter-${f.v}`}
            >
              {f.l} <span className="opacity-70">({counts[f.v] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-20 flex items-center justify-between flex-wrap gap-3 bg-primary/10 border border-primary/40 rounded-sm px-4 py-2.5" data-testid="bulk-toolbar">
          <div className="text-sm">
            <span className="font-semibold">{selectedIds.size}</span> padrão(ões) selecionado(s)
            <button onClick={selectAllVisible} className="ml-3 text-xs text-primary hover:underline" data-testid="bulk-select-visible">
              Selecionar todos visíveis ({filtered.length})
            </button>
            <button onClick={clearSelection} className="ml-3 text-xs text-muted-foreground hover:text-foreground" data-testid="bulk-clear">
              Limpar
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => bulkUpdate("ativo")}
              className="px-3 py-1.5 rounded-sm bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700" data-testid="bulk-activate">
              Ativar
            </button>
            <button onClick={() => bulkUpdate("rascunho")}
              className="px-3 py-1.5 rounded-sm bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600" data-testid="bulk-draft">
              Marcar rascunho
            </button>
            <button onClick={() => bulkUpdate("arquivado")}
              className="px-3 py-1.5 rounded-sm bg-muted text-foreground border border-border text-xs font-semibold hover:bg-muted/70" data-testid="bulk-archive">
              Arquivar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground overline">Carregando…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Nenhum padrão neste filtro.</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="px-4 py-2.5 bg-muted/50 border-b border-border">
                <div className="overline text-gold tracking-[0.22em]">{cat}</div>
                <div className="text-[10px] text-muted-foreground">{items.length} padrão{items.length !== 1 ? "es" : ""}</div>
              </div>
              <div className="divide-y divide-border">
                {items.map((p) => {
                  const sm = statusMeta(p.status);
                  const gm = gravityMeta(p.gravidade);
                  const isSel = selectedIds.has(p.id);
                  return (
                    <div key={p.id} className={`p-4 transition-colors ${isSel ? "bg-primary/5" : "hover:bg-muted/40"} ${p.is_alerta_medico ? "border-l-2 border-rose-500" : ""}`} data-testid={`pattern-row-${p.id}`}>
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleSelect(p.id)}
                          className="mt-0.5 p-0.5 text-muted-foreground hover:text-primary"
                          aria-label={isSel ? "Desmarcar" : "Selecionar"}
                          data-testid={`select-pattern-${p.id}`}
                        >
                          {isSel ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                        </button>
                        <div className="flex items-start justify-between gap-3 flex-wrap flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            {p.codigo_interno && (
                              <span className="px-1.5 py-0.5 rounded-sm bg-muted/50 text-[10px] font-mono text-muted-foreground">
                                {p.codigo_interno}
                              </span>
                            )}
                            <div className="font-heading font-semibold">{p.name}</div>
                            {p.is_alerta_medico && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-700 text-[10px] font-semibold uppercase tracking-wider" data-testid={`alerta-medico-${p.id}`}>
                                <Siren className="w-3 h-3" /> Alerta médico
                              </span>
                            )}
                            {p.gravidade && p.gravidade !== "eletivo" && (
                              <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${gm.cls}`}>
                                {gm.l}
                              </span>
                            )}
                            <button
                              onClick={() => cycleStatus(p)}
                              className={`px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase tracking-wider ${sm.cls}`}
                              title="Clique para alternar status"
                              data-testid={`status-toggle-${p.id}`}
                            >
                              {sm.l}
                            </button>
                            {p.nivel_confianca && (
                              <span className="px-2 py-0.5 rounded-sm border border-border bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                                Confiança: {CONFIDENCE_OPTIONS.find((c) => c.v === p.nivel_confianca)?.l || p.nivel_confianca}
                              </span>
                            )}
                            {p.subcategoria && (
                              <span className="px-2 py-0.5 rounded-sm border border-border bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                                {p.subcategoria}
                              </span>
                            )}
                            {(p.conditions || []).some((c) => (c.type || "marker") === "uso_de_farmaco") && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-gold/40 bg-gold/10 text-gold text-[10px] font-semibold uppercase tracking-wider"
                                title="Padrão com condição de fármaco em uso"
                                data-testid={`drug-badge-${p.id}`}
                              >
                                <Pill className="w-3 h-3" /> Fármaco
                              </span>
                            )}
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${
                                (p.trigger_count_30d || 0) > 0
                                  ? "border-gold/40 bg-gold/10 text-gold"
                                  : "border-border bg-muted/40 text-muted-foreground"
                              }`}
                              title="Disparos nos últimos 30 dias"
                              data-testid={`trigger-count-${p.id}`}
                            >
                              <TrendingUp className="w-3 h-3" /> {p.trigger_count_30d || 0} · 30d
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            <span className="font-semibold">Condições:</span>{" "}
                            {(p.conditions || []).map((c, i) => {
                              const ctype = c.type || "marker";
                              if (ctype === "uso_de_farmaco") {
                                return (
                                  <span key={i} className="inline-block mr-2">
                                    <span className="text-gold">Fármaco em uso:</span> {c.farmaco_codigo}
                                    {i < p.conditions.length - 1 ? " ·" : ""}
                                  </span>
                                );
                              }
                              return (
                                <span key={i} className="inline-block mr-2">
                                  {c.marker_name} <span className="text-gold">{compLabel(c.comparison)}</span>
                                  {c.comparison === "entre" && c.value_min != null && c.value_max != null && (
                                    <span className="text-gold"> [{c.value_min}–{c.value_max}]</span>
                                  )}
                                  {OPS_WITH_VALUE.has(c.comparison) && c.value != null && (
                                    <span className="text-gold"> {c.value}</span>
                                  )}
                                  {i < p.conditions.length - 1 ? " ·" : ""}
                                </span>
                              );
                            })}
                          </div>
                          {p.is_alerta_medico ? (
                            <div className="text-xs text-rose-700 mt-2 inline-flex items-center gap-1" data-testid={`alerta-mensagem-${p.id}`}>
                              <ShieldAlert className="w-3 h-3" /> Padrão sinaliza encaminhamento médico prioritário.
                            </div>
                          ) : (p.hipoteses && p.hipoteses.length > 0) ? (
                            <div className="text-xs text-foreground mt-2"><span className="overline text-muted-foreground">Hipóteses:</span> {p.hipoteses.join(" · ")}</div>
                          ) : p.nutritional_hypotheses ? (
                            <div className="text-xs text-foreground mt-2"><span className="overline text-muted-foreground">Hipóteses:</span> {p.nutritional_hypotheses}</div>
                          ) : null}
                          {(p.exames_complementares && p.exames_complementares.length > 0) ? (
                            <div className="text-xs text-foreground mt-1"><span className="overline text-muted-foreground">Exames complementares:</span> {p.exames_complementares.join(" · ")}</div>
                          ) : p.suggested_tests ? (
                            <div className="text-xs text-foreground mt-1"><span className="overline text-muted-foreground">Exames sugeridos:</span> {p.suggested_tests}</div>
                          ) : null}
                          {p.mecanismo && (
                            <details className="mt-2">
                              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary">Ver mecanismo</summary>
                              <p className="text-xs mt-1 text-foreground whitespace-pre-line">{p.mecanismo}</p>
                            </details>
                          )}
                          {p.tags && p.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {p.tags.map((t, i) => (
                                <span key={i} className="px-1.5 py-0.5 rounded-sm bg-muted/50 text-[10px] text-muted-foreground">#{t}</span>
                              ))}
                            </div>
                          )}
                          {p.fonte && (
                            <div className="text-xs text-muted-foreground mt-1"><span className="overline">Fonte:</span> {p.fonte}</div>
                          )}
                          {p.explanation && (
                            <details className="mt-2">
                              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary">Ver explicação</summary>
                              <p className="text-xs mt-1 text-foreground whitespace-pre-line">{p.explanation}</p>
                            </details>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setEditing(p)}
                            className="p-1.5 rounded-sm border border-border hover:bg-muted transition-colors"
                            data-testid={`edit-pattern-${p.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {p.created_by_professional_id && (
                            <button onClick={() => remove(p)}
                              className="p-1.5 rounded-sm border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                              data-testid={`delete-pattern-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {(editing || creating) && (
        <PatternModal
          initial={editing}
          markers={markers}
          drugs={drugs}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={async () => { setEditing(null); setCreating(false); await load(); }}
        />
      )}
      {importing && (
        <BulkImportModal
          onClose={() => setImporting(false)}
          onImported={async () => { setImporting(false); await load(); }}
        />
      )}
    </div>
  );
}
